import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FULLY_UNAUTHENTICATED_APIS } from '../constants/unauthenticated-apis';
import { RegistrationService } from '../contracts/registration-service.contract';
import {
  ApiResponse,
  RegistrationCodeEnabledResponse,
  RegistrationCompletedAuthenticatedResponse,
  RegistrationTokenDetailResponse
} from '../models/ApiResponse';
import { RestApiService } from '../stubs/rest-api-service.stub';

/**
 * Provides methods to register a user.
 */
@Injectable()
export class ApiRegistrationService extends RestApiService implements RegistrationService {
  /**
   * Initiates a new user registraion.
   *
   * @throws EmailAlreadyTakenException
   *
   * @param email The email address to send the verification mail to.
   */
  public async register(email: string, registrationCode?: string, captchaSolution?: string): Promise<void> {
    try {
      await firstValueFrom(
        this.httpClient.post<ApiResponse>(`${this.restUrl}${FULLY_UNAUTHENTICATED_APIS.AUTH_REGISTRATION_INIT_URI}`, {
          email,
          registrationCode,
          captchaSolution
        })
      );
    } catch (error) {
      this.convertToException(error);
    }
  }

  /**
   * Returns the details for a pending registration.
   *
   * @throws RegistrationNotFoundException
   * @throws TokenExpiredException
   *
   * @param registrationToken The registration token, obtained from the verification email.
   */
  public async getRegistrationDetails(registrationToken: string): Promise<RegistrationTokenDetailResponse> {
    try {
      const response = await firstValueFrom(
        this.httpClient.get<RegistrationTokenDetailResponse>(
          `${this.restUrl}${FULLY_UNAUTHENTICATED_APIS.AUTH_REGISTRATION_DETAILS_URI}${registrationToken}`
        )
      );
      return response;
    } catch (error) {
      this.convertToException(error);
    }
  }

  /**
   * Completes a users registration and authenticates at the same time
   * by returning the tokens required to access the software.
   *
   * @throws TokenExpiredException
   * @throws RegistrationNotFoundException
   *
   * @param registrationToken The registration token, obtained from the verification email.
   * @param password
   * @param firstName
   * @param lastName
   * @param title
   */
  public async completeRegistrationAndAuthenticate(
    registrationToken: string,
    password: string,
    firstName: string,
    lastName: string,
    title: string
  ): Promise<RegistrationCompletedAuthenticatedResponse> {
    try {
      const response = await firstValueFrom(
        this.httpClient.post<RegistrationCompletedAuthenticatedResponse>(
          `${this.restUrl}${FULLY_UNAUTHENTICATED_APIS.AUTH_REGISTRATION_COMPLETE_URI}${registrationToken}`,
          {
            token: registrationToken,
            password,
            firstName,
            lastName,
            title
          }
        )
      );
      return response;
    } catch (error) {
      this.convertToException(error);
    }
  }

  /**
   * Resends the verification email to a user which has already initiated a registration.
   *
   * @throws RegistrationNotFoundException
   *
   * @param email The email address of the users, which has been used in the registration.
   */
  public async resendVerificationMail(email: string): Promise<void> {
    try {
      await firstValueFrom(
        this.httpClient.post<ApiResponse>(`${this.restUrl}${FULLY_UNAUTHENTICATED_APIS.AUTH_REGISTRATION_RESEND_URI}`, { email })
      );
    } catch (error) {
      this.convertToException(error);
    }
  }

  /**
   * Checks if registraion code is enabled or not.
   */
  public async registraionCodeEnabled(): Promise<RegistrationCodeEnabledResponse> {
    try {
      const response = await firstValueFrom(
        this.httpClient.get<RegistrationCodeEnabledResponse>(`${this.restUrl}${FULLY_UNAUTHENTICATED_APIS.AUTH_REGISTRATION_USECODE_URI}`)
      );
      return response;
    } catch (error) {
      this.convertToException(error);
    }
  }
}
